// NPS and feedback.  /analytics/nps
//
// This is the console's user-satisfaction screen, and it is worth being exact
// about what it holds. The survey asks a 0 to 10 Net Promoter question. Each
// answer is stored as a bucket, 0-5 detractor / 6-8 passive / 9-10 promoter,
// and the headline is promoters minus detractors as a percentage, so it runs
// from -100 to +100.
//
// The client scope asks for a satisfaction score out of 5 with a 4.2 target.
// That is a different instrument. NPS throws away the value inside each bucket
// and is a difference of proportions rather than a mean, so there is no way to
// restate one as the other. Everything here is therefore labelled NPS, and the
// 4.2 out of 5 target is reported as not measured on the chatbot analytics
// screen instead of being faked from this number.

import { useState } from "react";

import { http, qs } from "../api";
import {
  Badge,
  Banner,
  Empty,
  Pager,
  SkeletonRows,
  Table,
  useAsync,
  type BadgeTone,
  type Column,
} from "../ui";
import { DonutCard, StatTile, type BarDatum } from "../charts";
import {
  ExportMenu,
  NotMeasuredTile,
  RangeControls,
  SCAM_TYPE_LABELS,
  SCAM_TYPE_OPTIONS,
  formatScamType,
  nf,
  stamp,
  useRange,
  type RangePreset,
} from "./shared";

interface NPSFeedbackItem {
  session_id: string;
  contact_id: string;
  contact_name: string;
  contact_phone: string;
  score: string;
  score_label: string;
  feedback: string;
  feedback_source: "text" | "voice" | "";
  flow_name: string;
  scam_classification: string;
  scam_subtype: string;
  completed_at: string | null;
  created_at: string;
}

interface NPSSummary {
  total_responses: number;
  detractors: number;
  passives: number;
  promoters: number;
  nps_score: number;
}

interface NPSResponse {
  summary: NPSSummary;
  feedbacks: NPSFeedbackItem[];
  total: number;
  page: number;
  limit: number;
}

interface NPSScamRow {
  scam_classification: string;
  total_responses: number;
  detractors: number;
  passives: number;
  promoters: number;
  nps_score: number;
}

const NPS_PAGE_SIZE = 20;

const SCORE_OPTIONS: { value: string; label: string }[] = [
  { value: "all", label: "All scores" },
  { value: "nps_score_0_5", label: "Detractors (0-5)" },
  { value: "nps_score_6_8", label: "Passives (6-8)" },
  { value: "nps_score_9_10", label: "Promoters (9-10)" },
];

function scoreTone(score: string): BadgeTone {
  if (score === "nps_score_0_5") return "critical";
  if (score === "nps_score_9_10") return "good";
  return "warning";
}

export function NPSFeedbackView() {
  const { preset, setPreset, draft, setDraft, apply, range } = useRange({});
  const [score, setScore] = useState("all");
  const [scam, setScam] = useState("all");
  const [offset, setOffset] = useState(0);

  // Changing a filter or the range must land the operator back on page 1;
  // page 4 of the old result set is meaningless against the new one.
  const changeScore = (next: string) => {
    setScore(next);
    setOffset(0);
  };
  const changeScam = (next: string) => {
    setScam(next);
    setOffset(0);
  };
  const changePreset = (next: RangePreset) => {
    setPreset(next);
    setOffset(0);
  };
  const applyRange = () => {
    apply();
    setOffset(0);
  };

  const listQuery = useAsync(
    () =>
      http.get<NPSResponse>(
        `/analytics/nps${qs({
          from: range.from,
          to: range.to,
          page: Math.floor(offset / NPS_PAGE_SIZE) + 1,
          limit: NPS_PAGE_SIZE,
          score: score === "all" ? "" : score,
          scam: scam === "all" ? "" : scam,
        })}`
      ),
    [range.from, range.to, offset, score, scam]
  );

  // The breakdown deliberately ignores the score and scam filters: it is the
  // whole period's shape, and re-filtering it would make the table agree with
  // the list instead of explaining it.
  const breakdownQuery = useAsync(
    () =>
      http.get<NPSScamRow[]>(`/analytics/nps/scam-breakdown${qs({ from: range.from, to: range.to })}`),
    [range.from, range.to]
  );

  const data = listQuery.data;
  const summary = data?.summary;
  const responses = summary?.total_responses ?? 0;

  const distribution: BarDatum[] =
    summary && responses > 0
      ? [
          { label: "Detractors (0-5)", value: summary.detractors },
          { label: "Passives (6-8)", value: summary.passives },
          { label: "Promoters (9-10)", value: summary.promoters },
        ]
      : [];

  const breakdownColumns: Column<NPSScamRow>[] = [
    {
      key: "scam_classification",
      header: "Scam type",
      sortable: true,
      render: (r) => SCAM_TYPE_LABELS[r.scam_classification] ?? r.scam_classification,
    },
    { key: "total_responses", header: "Responses", align: "right", sortable: true },
    { key: "detractors", header: "Detractors", align: "right", sortable: true },
    { key: "passives", header: "Passives", align: "right", sortable: true },
    { key: "promoters", header: "Promoters", align: "right", sortable: true },
    {
      key: "nps_score",
      header: "NPS",
      align: "right",
      sortable: true,
      // A row with no responses has no score. The server sends 0 for it, and 0
      // is a real NPS, so the count decides whether the figure is printed.
      render: (r) =>
        r.total_responses > 0 ? (
          <strong className="num">{r.nps_score}</strong>
        ) : (
          <span className="muted">not measured</span>
        ),
    },
  ];

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>NPS feedback</h1>
          <div className="sub">
            Net Promoter Score from a 0 to 10 question, reported between -100 and +100. This is not
            the scope's satisfaction score out of 5.
          </div>
        </div>
        <div className="page-actions">
          <select
            value={score}
            aria-label="Score bucket"
            style={{ width: 180 }}
            onChange={(e) => changeScore(e.target.value)}
          >
            {SCORE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <select
            value={scam}
            aria-label="Scam type"
            style={{ width: 200 }}
            onChange={(e) => changeScam(e.target.value)}
          >
            <option value="all">All scam types</option>
            {SCAM_TYPE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <RangeControls
            preset={preset}
            onPreset={changePreset}
            draft={draft}
            onDraft={setDraft}
            onApply={applyRange}
          />
          <ExportMenu
            snapshot="nps"
            range={range}
            rawDataset="nps_feedback"
            extra={{
              ...(score !== "all" ? { score } : {}),
              ...(scam !== "all" ? { scam } : {}),
            }}
          />
        </div>
      </div>

      {listQuery.error && (
        <Banner tone="critical" traceId={listQuery.error.traceId}>
          Could not load NPS feedback: {listQuery.error.message}
        </Banner>
      )}
      {breakdownQuery.error && (
        <Banner tone="warning" traceId={breakdownQuery.error.traceId}>
          The scam breakdown did not load: {breakdownQuery.error.message}
        </Banner>
      )}

      {listQuery.loading ? (
        <div className="grid c4">
          {[0, 1, 2, 3, 4].map((i) => (
            <div className="card" key={i}>
              <SkeletonRows n={2} cols={2} />
            </div>
          ))}
        </div>
      ) : (
        <>
          <div className="grid c4" style={{ marginBottom: "var(--sp-4)" }}>
            {/* An NPS of 0 is a real result: as many detractors as promoters.
                No responses at all is not, so the count gates the score. */}
            {responses > 0 ? (
              <StatTile
                label="NPS score"
                value={summary?.nps_score ?? 0}
                deltaLabel="-100 to +100 scale"
              />
            ) : (
              <NotMeasuredTile label="NPS score" why="no survey responses in this range" />
            )}
            <StatTile label="Total responses" value={responses} />
            <StatTile label="Detractors" value={summary?.detractors ?? 0} />
            <StatTile label="Passives" value={summary?.passives ?? 0} />
            <StatTile label="Promoters" value={summary?.promoters ?? 0} />
          </div>

          <div className="grid c2" style={{ marginBottom: "var(--sp-4)" }}>
            <div className="card">
              {distribution.length === 0 ? (
                <Empty
                  title="No score distribution"
                  body="No survey response landed in this range."
                  icon="☺"
                />
              ) : (
                <DonutCard
                  title="Score distribution"
                  data={distribution}
                  centerCaption="responses"
                  note="Every response in the period."
                />
              )}
            </div>
            <div className="card flush">
              <div className="card-head">
                <h3>NPS by scam type</h3>
                <span className="muted">Not affected by the filters above</span>
              </div>
              <Table
                columns={breakdownColumns}
                rows={breakdownQuery.data ?? []}
                loading={breakdownQuery.loading}
                empty="No scam-classified NPS responses in this range"
              />
            </div>
          </div>

          <div className="card">
            <div className="row" style={{ marginBottom: "var(--sp-3)" }}>
              <h3>User feedback</h3>
              <span className="spacer" />
              <span className="muted">{nf.format(data?.total ?? 0)} total responses</span>
            </div>

            {(data?.feedbacks.length ?? 0) === 0 ? (
              <Empty
                title="No feedback collected yet"
                body="NPS surveys are sent after each conversation ends."
                icon="☺"
              />
            ) : (
              <>
                <div className="col">
                  {(data?.feedbacks ?? []).map((item) => (
                    <div className="card" key={item.session_id}>
                      <div className="row">
                        <span className="strong">{item.contact_name || item.contact_phone}</span>
                        <Badge tone={scoreTone(item.score)}>{item.score_label}</Badge>
                        {item.scam_classification && (
                          <Badge tone="neutral">
                            {formatScamType(item.scam_classification, item.scam_subtype)}
                          </Badge>
                        )}
                        <span className="spacer" />
                        <span className="muted nowrap">{stamp(item.created_at)}</span>
                      </div>
                      {item.feedback ? (
                        <p className="prewrap" style={{ marginTop: "var(--sp-2)" }}>
                          {item.feedback_source === "voice" && (
                            <span className="muted" title="Transcribed from voice note">
                              ◉{" "}
                            </span>
                          )}
                          {item.feedback}
                        </p>
                      ) : (
                        <p className="muted" style={{ marginTop: "var(--sp-2)" }}>
                          No written feedback
                        </p>
                      )}
                      <div className="muted" style={{ marginTop: "var(--sp-2)" }}>
                        Source: {item.flow_name || "Wiki conversation"}
                      </div>
                    </div>
                  ))}
                </div>
                <Pager
                  total={data?.total ?? 0}
                  limit={NPS_PAGE_SIZE}
                  offset={offset}
                  onChange={setOffset}
                  itemName="responses"
                />
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}
